// File: src/components/ExcelImport.js
// Excel import modal component - imports race results from Excel files

import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../App';
import { calculatePointsForPosition, calculateQualificationPoints } from '../utils/utils';
import { useLanguage } from '../contexts/LanguageContext';
import { handleUnauthorized } from '../utils/session';

function ExcelImport({ raceId, seriesClass, token, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setMessage('');
    setPreview(null);

    try {
      const data = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setMessage(t('noDataFoundInExcel'));
        return;
      }

      const results = [];
      const newDrivers = [];
      const unmatchedRows = [];

      for (const row of jsonData) {
        const driverName = (row.Driver || row.Name || row.driver || row.name || '').trim();
        const position = parseInt(row.Position || row.Pos || row.pos || row.place);
        const qualPosition = parseInt(row.Qual || row.Qualification || row.Q || '');

        if (!driverName || !position) {
          unmatchedRows.push({ row, reason: t('missingDriverNameOrPosition') });
          continue;
        }

        const existingDriver = seriesClass?.drivers?.find(
          d => d.name.toLowerCase().trim() === driverName.toLowerCase()
        );

        const points = calculatePointsForPosition(position);
        const qualPoints = qualPosition && seriesClass?.calculate_qualification_points
          ? calculateQualificationPoints(qualPosition)
          : 0;

        if (existingDriver) {
          results.push({
            driver_id: existingDriver.id,
            driver_name: existingDriver.name,
            position,
            qualification_position: qualPosition || null,
            points,
            qualification_points: qualPoints,
            isNew: false
          });
        } else {
          newDrivers.push(driverName);
          results.push({
            driver_name: driverName,
            position,
            qualification_position: qualPosition || null,
            points,
            qualification_points: qualPoints,
            isNew: true
          });
        }
      }

      setPreview({
        results,
        newDrivers,
        unmatchedRows
      });

      if (results.length > 0) {
        setMessage(t('successfullyParsedResults').replace('{count}', results.length));
      }

    } catch (error) {
      setMessage(t('errorReadingExcel') + error.message);
    }
  };

  const importResults = async () => {
    if (!preview || preview.results.length === 0) return;

    setLoading(true);

    try {
      // Create new drivers
      const newDriverIds = {};
      for (const driverName of preview.newDrivers) {
        const response = await fetch(`${API_BASE_URL}/drivers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: driverName,
            series_class_id: seriesClass.id
          })
        });

        if (handleUnauthorized(response)) return;

        if (response.ok) {
          const newDriver = await response.json();
          newDriverIds[driverName] = newDriver.id;
        }
      }

      // Import all results
      // Prepare all rankings for batch update - MATCH BACKEND FORMAT
      const rankings = preview.results.map(result => {
        const driverId = result.isNew ? newDriverIds[result.driver_name] : result.driver_id;
        return {
          driverId: driverId,  // camelCase!
          position: result.position,
          qualificationPosition: result.qualification_position,  // camelCase!
          points: result.points,
          qualificationPoints: result.qualification_points  // camelCase!
        };
      });

      // Send all rankings in one request
      const rankingsResponse = await fetch(`${API_BASE_URL}/races/${raceId}/rankings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rankings })
      });

      if (handleUnauthorized(rankingsResponse)) return;
      if (!rankingsResponse.ok) {
        const data = await rankingsResponse.json().catch(() => ({}));
        setMessage(t('importFailed') + (data.error || rankingsResponse.status));
        return;
      }

      setMessage(
        t('successfullyParsedResults').replace('{count}', preview.results.length) +
        (preview.newDrivers.length > 0
          ? `\n\n${preview.newDrivers.length} ${t('newDriversWillBeCreated')}\n${preview.newDrivers.join(', ')}\n\n${t('tipAddClubAndBirthYear')}`
          : '')
      );

      onSuccess();
      
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      setMessage(t('importFailed') + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-line">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-ink">{t('importFromExcel')}</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-3 rounded">
            <p className="font-semibold mb-2">{t('excelFileFormat')}</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{t('columnDriver')}</li>
              <li>{t('columnPosition')}</li>
              <li>{t('columnQual')}</li>
            </ul>
            <p className="mt-2 text-sm">{t('driversNotInSeriesCreated')}</p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-ink-muted mb-2 font-semibold">{t('chooseExcelFile')}</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-accent file:text-accent-ink file:cursor-pointer hover:file:opacity-90"
            />
          </div>

          {/* Status Messages */}
          {message && (
            <div className={`px-4 py-3 rounded ${
              message.startsWith('✓')
                ? 'bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200'
                : 'bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200'
            }`}>
              <pre className="whitespace-pre-wrap font-sans">{message}</pre>
            </div>
          )}

          {/* Preview Results */}
          {preview && preview.results.length > 0 && (
            <div className="border border-line rounded p-4">
              <h4 className="text-lg font-bold text-ink mb-3">{t('preview')}</h4>

              {/* Summary */}
              <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-2 rounded mb-4">
                <strong>✓ {preview.results.length} {t('resultsReadyToImport')}</strong>
                {preview.newDrivers.length > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="bg-accent text-accent-ink px-2 py-1 rounded text-xs">{t('new')}</span>
                    {' '}{preview.newDrivers.length} {t('newDriversWillBeCreated')} {preview.newDrivers.join(', ')}
                  </div>
                )}
              </div>

              {/* New Drivers Notice */}
              {preview.newDrivers.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded mb-4 text-sm">
                  {t('rememberToAddClubAndBirthYear')}
                </div>
              )}

              {/* Unmatched Rows Warning */}
              {preview.unmatchedRows.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-500/20 border border-orange-500 text-orange-800 dark:text-orange-200 px-4 py-3 rounded mb-4 text-sm">
                  <strong>⚠ {preview.unmatchedRows.length} {t('rowsSkipped')}</strong>
                  <ul className="mt-2 list-disc list-inside">
                    {preview.unmatchedRows.slice(0, 3).map((item, idx) => (
                      <li key={idx}>{item.reason}</li>
                    ))}
                    {preview.unmatchedRows.length > 3 && (
                      <li>...{t('andMore').replace('{count}', preview.unmatchedRows.length - 3)}</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Results Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left text-ink-muted pb-2 pr-4">{t('driver')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('position')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('points')}</th>
                      {seriesClass?.calculate_qualification_points && (
                        <>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qualPos')}</th>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qualPts')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.results.map((result, idx) => (
                      <tr key={idx} className="border-b border-line">
                        <td className="py-2 pr-4">
                          <span className="text-ink">{result.driver_name}</span>
                          {result.isNew && (
                            <span className="ml-2 bg-accent text-accent-ink px-2 py-0.5 rounded text-xs">{t('new')}</span>
                          )}
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-ink font-semibold">P{result.position}</span>
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-yellow-700 dark:text-yellow-400 font-mono-num">{result.points}</span>
                        </td>
                        {seriesClass?.calculate_qualification_points && (
                          <>
                            <td className="text-center py-2 px-2">
                              {result.qualification_position ? (
                                <span className="text-green-700 dark:text-green-400">Q{result.qualification_position}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                            <td className="text-center py-2 px-2">
                              {result.qualification_points > 0 ? (
                                <span className="text-green-700 dark:text-green-400 font-mono-num">{result.qualification_points}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Import Button */}
              <button
                onClick={importResults}
                disabled={loading}
                className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:bg-paper-alt disabled:text-ink-muted text-white py-3 rounded font-semibold flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('importing')}
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    {t('importResults').replace('{count}', preview.results.length)}{preview.newDrivers.length > 0 ? ` (${preview.newDrivers.length} ${t('newDrivers')})` : ''}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExcelImport;